import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, authAPI, productsAPI, contentAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Package, ShoppingCart, Users, DollarSign, Plus, Edit, Trash2, Search,
  AlertTriangle, FileText, Sparkles, Save, LayoutDashboard, TrendingUp,
  Download, BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Upload,
  Image as ImageIcon, Video, RefreshCw, Calendar, Filter, ChevronDown,
  UserCog, Shield, Bot, Truck
} from 'lucide-react';
import Layout from '@/components/Layout';
import { ShippingZonesManager } from '@/features/admin/shipping';
import { toast } from 'sonner';
import reportsAPI from '@/features/admin/reports/reportsApi';
import { AIContentManager } from '@/features/admin/ai';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dashboard summary
  const [dashboardSummary, setDashboardSummary] = useState(null);
  
  // Products state
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', brand: '', category: '', stock: '', 
    images: '', condition: 'new', supplier: '', specs: ''
  });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  
  // Reports state
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('daily');
  const [loadingReports, setLoadingReports] = useState(false);
  
  // Content state
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({ title: '', content: {} });
  const [generating, setGenerating] = useState(false);
  
  // Admin users state
  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    checkAdminAndLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const user = await authAPI.getMe();
      const adminCheck = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/check`, {
        credentials: 'include'
      }).then(r => r.json());
      
      if (!adminCheck.is_admin) {
        toast.error('Admin access required');
        navigate('/products');
        return;
      }
      
      setIsAdmin(true);
      loadDashboardData();
    } catch (error) {
      toast.error('Please login first');
      navigate('/login');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, productsData, ordersData, customersData] = await Promise.all([
        adminAPI.getStats(),
        productsAPI.getAll({ limit: 2000 }),
        adminAPI.getAllOrders(),
        adminAPI.getCustomers()
      ]);
      setStats(statsData);
      setProducts(productsData);
      setOrders(ordersData);
      setCustomers(customersData);
      
      // Load dashboard summary
      try {
        const summary = await reportsAPI.getDashboardSummary();
        setDashboardSummary(summary);
      } catch (e) {
        console.log('Dashboard summary not available');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const [sales, inventory] = await Promise.all([
        reportsAPI.getSalesReport(reportPeriod),
        reportsAPI.getInventoryReport()
      ]);
      setSalesReport(sales);
      setInventoryReport(inventory);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  };

  const handleExportReport = async (reportType) => {
    try {
      const blob = await reportsAPI.exportReport(reportType);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setUploadingMedia(true);
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    formData.append('folder', 'products');
    
    try {
      const csrfToken = document.cookie.match(/csrf_token=([^;]+)/)?.[1] || '';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload/multiple`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      if (response.ok) {
        const uploaded = await response.json();
        const urls = uploaded.map(m => m.secure_url).join(', ');
        setProductForm(prev => ({
          ...prev,
          images: prev.images ? `${prev.images}, ${urls}` : urls
        }));
        toast.success(`${uploaded.length} file(s) uploaded`);
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const specs = {};
      if (productForm.specs) {
        productForm.specs.split('\n').forEach(line => {
          const [key, value] = line.split(':').map(s => s.trim());
          if (key && value) specs[key] = value;
        });
      }
      
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock),
        images: productForm.images.split(',').map(s => s.trim()).filter(Boolean),
        specs
      };
      
      if (editingProduct) {
        await productsAPI.update(editingProduct.product_id, productData);
        toast.success('Product updated');
      } else {
        await productsAPI.create(productData);
        toast.success('Product created');
      }
      
      loadDashboardData();
      resetProductForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsAPI.delete(productId);
      toast.success('Product deleted');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status);
      toast.success('Order status updated');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleGenerateContent = async (pageType) => {
    setGenerating(true);
    try {
      const response = await contentAPI.generateContent(
        `Generate professional content for a ${pageType} page for Bamo Tech Sphere, a premium luxury technology store in Kenya.`,
        pageType
      );
      setContentForm(prev => ({
        ...prev,
        content: { generated: response.generated_content }
      }));
      toast.success('Content generated! Review and save.');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveContent = async (pageId) => {
    try {
      await contentAPI.updatePage(pageId, contentForm);
      toast.success('Content saved');
      setEditingContent(null);
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const resetProductForm = () => {
    setProductForm({ 
      name: '', description: '', price: '', brand: '', category: '', stock: '', 
      images: '', condition: 'new', supplier: '', specs: ''
    });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const editProduct = (product) => {
    const specsText = product.specs ? 
      Object.entries(product.specs).map(([k, v]) => `${k}: ${v}`).join('\n') : '';
    
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      brand: product.brand,
      category: product.category,
      stock: product.stock.toString(),
      images: product.images?.join(', ') || '',
      condition: product.condition || 'new',
      supplier: product.supplier || '',
      specs: specsText
    });
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.brand.toLowerCase().includes(productSearch.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-32 rounded-xl"></div>)}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) return null;

  return (
    <Layout>
      <div className="section-padding" data-testid="admin-dashboard">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-muted-foreground">POS-Style Management System</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadDashboardData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card className="luxury-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Today</p>
                    <p className="text-xl font-bold">KES {(dashboardSummary?.today?.revenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-green-600">{dashboardSummary?.today?.orders || 0} orders</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="luxury-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">This Week</p>
                    <p className="text-xl font-bold">KES {(dashboardSummary?.this_week?.revenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-blue-600">{dashboardSummary?.this_week?.orders || 0} orders</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="luxury-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">This Month</p>
                    <p className="text-xl font-bold">KES {(dashboardSummary?.this_month?.revenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-purple-600">{dashboardSummary?.this_month?.orders || 0} orders</p>
                  </div>
                  <PieChart className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="luxury-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Products</p>
                    <p className="text-xl font-bold">{stats?.total_products || 0}</p>
                    <p className="text-xs text-orange-600">{dashboardSummary?.inventory?.low_stock || 0} low stock</p>
                  </div>
                  <Package className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="luxury-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Customers</p>
                    <p className="text-xl font-bold">{stats?.total_users || 0}</p>
                    <p className="text-xs text-cyan-600">Active users</p>
                  </div>
                  <Users className="w-8 h-8 text-cyan-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="luxury-card bg-gradient-to-br from-accent/20 to-accent/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Revenue</p>
                    <p className="text-xl font-bold">KES {(stats?.total_revenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-accent">All time</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-accent" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8">
              <TabsTrigger value="overview"><LayoutDashboard className="w-4 h-4 mr-1 hidden sm:inline" />Overview</TabsTrigger>
              <TabsTrigger value="products"><Package className="w-4 h-4 mr-1 hidden sm:inline" />Inventory</TabsTrigger>
              <TabsTrigger value="orders"><ShoppingCart className="w-4 h-4 mr-1 hidden sm:inline" />Orders</TabsTrigger>
              <TabsTrigger value="reports"><BarChart3 className="w-4 h-4 mr-1 hidden sm:inline" />Reports</TabsTrigger>
              <TabsTrigger value="customers"><Users className="w-4 h-4 mr-1 hidden sm:inline" />Customers</TabsTrigger>
              <TabsTrigger value="shipping"><Truck className="w-4 h-4 mr-1 hidden sm:inline" />Shipping</TabsTrigger>
              <TabsTrigger value="ai-content"><Bot className="w-4 h-4 mr-1 hidden sm:inline" />AI Content</TabsTrigger>
              <TabsTrigger value="content"><FileText className="w-4 h-4 mr-1 hidden sm:inline" />Pages</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Orders */}
                <Card className="luxury-card">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Recent Orders
                      <Button variant="ghost" size="sm" onClick={() => setActiveTab('orders')}>View All</Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {orders.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No orders yet</p>
                    ) : (
                      <div className="space-y-3">
                        {orders.slice(0, 5).map(order => (
                          <div key={order.order_id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{order.order_id.slice(0, 12)}...</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(order.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">KES {order.total?.toLocaleString()}</p>
                              <Badge variant={order.status === 'delivered' ? 'default' : 'secondary'} className="text-xs">
                                {order.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Low Stock Alert */}
                <Card className="luxury-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      Low Stock Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.low_stock_products?.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">All products well stocked</p>
                    ) : (
                      <div className="space-y-3">
                        {stats?.low_stock_products?.slice(0, 5).map(product => (
                          <div key={product.product_id} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                            <div className="flex items-center gap-3">
                              <img 
                                src={product.images?.[0] || ''} 
                                alt="" 
                                className="w-10 h-10 object-cover rounded"
                              />
                              <div>
                                <p className="font-medium text-sm">{product.name}</p>
                                <p className="text-xs text-muted-foreground">{product.brand}</p>
                              </div>
                            </div>
                            <Badge variant="destructive">{product.stock} left</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Products/Inventory Tab */}
            <TabsContent value="products">
              <Card className="luxury-card">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <CardTitle>Inventory Management</CardTitle>
                      <CardDescription>Manage products with images, videos, and specifications</CardDescription>
                    </div>
                    <Button onClick={() => setShowProductForm(true)} className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />Add Product
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {showProductForm && (
                    <form onSubmit={handleProductSubmit} className="mb-8 p-6 bg-secondary/20 rounded-xl">
                      <h3 className="text-lg font-semibold mb-4">{editingProduct ? 'Edit Product' : 'New Product'}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <Label>Product Name *</Label>
                          <Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Brand *</Label>
                          <Input value={productForm.brand} onChange={e => setProductForm({...productForm, brand: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Supplier</Label>
                          <Input value={productForm.supplier} onChange={e => setProductForm({...productForm, supplier: e.target.value})} placeholder="Supplier name" />
                        </div>
                        <div>
                          <Label>Price (KES) *</Label>
                          <Input type="number" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Stock Quantity *</Label>
                          <Input type="number" value={productForm.stock} onChange={e => setProductForm({...productForm, stock: e.target.value})} required />
                        </div>
                        <div>
                          <Label>Category *</Label>
                          <Select value={productForm.category} onValueChange={v => setProductForm({...productForm, category: v})}>
                            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="refurbished-laptops">Refurbished Laptops</SelectItem>
                              <SelectItem value="laptops">New Laptops</SelectItem>
                              <SelectItem value="desktops">Desktops & Workstations</SelectItem>
                              <SelectItem value="phones">Mobile Phones</SelectItem>
                              <SelectItem value="security">CCTV & Security</SelectItem>
                              <SelectItem value="printers">Printers</SelectItem>
                              <SelectItem value="accessories">Accessories</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Condition *</Label>
                          <Select value={productForm.condition} onValueChange={v => setProductForm({...productForm, condition: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="refurbished">Refurbished</SelectItem>
                              <SelectItem value="open-box">Open Box</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2">
                          <Label>Media Upload</Label>
                          <div className="flex gap-2 mt-2">
                            <label className="flex-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-accent transition-colors">
                              <input 
                                type="file" 
                                multiple 
                                accept="image/*,video/*" 
                                onChange={handleMediaUpload}
                                className="hidden"
                              />
                              {uploadingMedia ? (
                                <RefreshCw className="w-5 h-5 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-5 h-5" />
                                  <span className="text-sm">Upload Images/Videos</span>
                                </>
                              )}
                            </label>
                          </div>
                        </div>
                        <div className="md:col-span-3">
                          <Label>Image URLs (comma separated)</Label>
                          <Input 
                            value={productForm.images} 
                            onChange={e => setProductForm({...productForm, images: e.target.value})} 
                            placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <Label>Description *</Label>
                          <Textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} required rows={3} />
                        </div>
                        <div className="md:col-span-3">
                          <Label>Specifications (one per line, format: Key: Value)</Label>
                          <Textarea 
                            value={productForm.specs} 
                            onChange={e => setProductForm({...productForm, specs: e.target.value})} 
                            placeholder="Processor: Intel Core i7&#10;RAM: 16GB&#10;Storage: 512GB SSD"
                            rows={4}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button type="submit" className="btn-primary"><Save className="w-4 h-4 mr-2" />Save Product</Button>
                        <Button type="button" variant="outline" onClick={resetProductForm}>Cancel</Button>
                      </div>
                    </form>
                  )}

                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        placeholder="Search products..."
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-4">Product</th>
                          <th className="text-left p-4 hidden md:table-cell">Category</th>
                          <th className="text-left p-4">Price</th>
                          <th className="text-left p-4">Stock</th>
                          <th className="text-left p-4 hidden lg:table-cell">Condition</th>
                          <th className="text-left p-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.slice(0, 20).map(product => (
                          <tr key={product.product_id} className="border-b hover:bg-secondary/10">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <img src={product.images?.[0] || ''} alt="" className="w-12 h-12 object-cover rounded" />
                                <div>
                                  <p className="font-medium line-clamp-1">{product.name}</p>
                                  <p className="text-sm text-muted-foreground">{product.brand}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              <Badge variant="outline">{product.category}</Badge>
                            </td>
                            <td className="p-4 font-mono">KES {product.price?.toLocaleString()}</td>
                            <td className="p-4">
                              <Badge variant={product.stock < 10 ? 'destructive' : 'secondary'}>
                                {product.stock}
                              </Badge>
                            </td>
                            <td className="p-4 hidden lg:table-cell">
                              <Badge variant="outline">{product.condition || 'New'}</Badge>
                            </td>
                            <td className="p-4">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => editProduct(product)}>
                                  <Edit size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteProduct(product.product_id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <Card className="luxury-card">
                <CardHeader>
                  <CardTitle>Orders Management</CardTitle>
                  <CardDescription>Track and manage all orders</CardDescription>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No orders yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-4">Order ID</th>
                            <th className="text-left p-4">Date</th>
                            <th className="text-left p-4 hidden md:table-cell">Items</th>
                            <th className="text-left p-4">Total</th>
                            <th className="text-left p-4">Status</th>
                            <th className="text-left p-4">Update</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map(order => (
                            <tr key={order.order_id} className="border-b hover:bg-secondary/10">
                              <td className="p-4 font-mono text-sm">{order.order_id.slice(0, 12)}...</td>
                              <td className="p-4 text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                              <td className="p-4 hidden md:table-cell">{order.items?.length || 0} items</td>
                              <td className="p-4 font-bold">KES {order.total?.toLocaleString()}</td>
                              <td className="p-4">
                                <Badge variant={
                                  order.status === 'delivered' ? 'default' :
                                  order.status === 'shipped' ? 'secondary' :
                                  order.status === 'cancelled' ? 'destructive' :
                                  'outline'
                                }>
                                  {order.status}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <Select value={order.status} onValueChange={v => handleUpdateOrderStatus(order.order_id, v)}>
                                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="processing">Processing</SelectItem>
                                    <SelectItem value="shipped">Shipped</SelectItem>
                                    <SelectItem value="delivered">Delivered</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <div className="space-y-6">
                {/* Report Controls */}
                <Card className="luxury-card">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <BarChart3 className="w-5 h-5" />
                          POS-Style Reports
                        </CardTitle>
                        <CardDescription>Generate and export business reports</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Select value={reportPeriod} onValueChange={setReportPeriod}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={loadReports} disabled={loadingReports}>
                          {loadingReports ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Load Reports'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Sales Report */}
                {salesReport && (
                  <Card className="luxury-card">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Sales Report</CardTitle>
                        <Button variant="outline" size="sm" onClick={() => handleExportReport('sales')}>
                          <Download className="w-4 h-4 mr-2" />Export CSV
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Total Revenue</p>
                          <p className="text-2xl font-bold text-green-600">KES {salesReport.summary?.total_revenue?.toLocaleString()}</p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Total Orders</p>
                          <p className="text-2xl font-bold text-blue-600">{salesReport.summary?.total_orders}</p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-950/20 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Avg Order Value</p>
                          <p className="text-2xl font-bold text-purple-600">KES {salesReport.summary?.average_order_value?.toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {/* Payment Method Breakdown */}
                      <h4 className="font-semibold mb-3">Payment Method Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {salesReport.payment_breakdown?.map(method => (
                          <div key={method.method} className="bg-secondary/30 p-3 rounded-lg">
                            <p className="text-sm font-medium capitalize">{method.method}</p>
                            <p className="text-lg font-bold">KES {method.revenue?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">{method.count} orders</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Inventory Report */}
                {inventoryReport && (
                  <Card className="luxury-card">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Inventory Report</CardTitle>
                        <Button variant="outline" size="sm" onClick={() => handleExportReport('inventory')}>
                          <Download className="w-4 h-4 mr-2" />Export CSV
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-secondary/30 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Total Products</p>
                          <p className="text-2xl font-bold">{inventoryReport.summary?.total_products}</p>
                        </div>
                        <div className="bg-secondary/30 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Total Stock Units</p>
                          <p className="text-2xl font-bold">{inventoryReport.summary?.total_stock_units?.toLocaleString()}</p>
                        </div>
                        <div className="bg-accent/10 p-4 rounded-xl">
                          <p className="text-sm text-muted-foreground">Inventory Value</p>
                          <p className="text-2xl font-bold text-accent">KES {inventoryReport.summary?.total_inventory_value?.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Stock Levels */}
                      <h4 className="font-semibold mb-3">Stock Levels</h4>
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-red-600">{inventoryReport.stock_levels?.out_of_stock}</p>
                          <p className="text-sm text-muted-foreground">Out of Stock</p>
                        </div>
                        <div className="bg-yellow-50 dark:bg-yellow-950/20 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-yellow-600">{inventoryReport.stock_levels?.low_stock}</p>
                          <p className="text-sm text-muted-foreground">Low Stock</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-lg text-center">
                          <p className="text-2xl font-bold text-green-600">{inventoryReport.stock_levels?.healthy_stock}</p>
                          <p className="text-sm text-muted-foreground">Healthy</p>
                        </div>
                      </div>

                      {/* Category Breakdown */}
                      <h4 className="font-semibold mb-3">By Category</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Category</th>
                              <th className="text-right p-2">Products</th>
                              <th className="text-right p-2">Stock</th>
                              <th className="text-right p-2">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inventoryReport.category_breakdown?.map(cat => (
                              <tr key={cat.category} className="border-b">
                                <td className="p-2 capitalize">{cat.category}</td>
                                <td className="p-2 text-right">{cat.count}</td>
                                <td className="p-2 text-right">{cat.stock}</td>
                                <td className="p-2 text-right font-mono">KES {cat.value?.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers">
              <Card className="luxury-card">
                <CardHeader>
                  <CardTitle>Customer Management</CardTitle>
                  <CardDescription>View and manage customer accounts</CardDescription>
                </CardHeader>
                <CardContent>
                  {customers.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No customers yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-4">Customer</th>
                            <th className="text-left p-4">Email</th>
                            <th className="text-left p-4 hidden md:table-cell">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.map(customer => (
                            <tr key={customer.user_id} className="border-b hover:bg-secondary/10">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                    {customer.picture ? (
                                      <img src={customer.picture} alt="" className="w-10 h-10 rounded-full" />
                                    ) : (
                                      <Users size={20} className="text-accent" />
                                    )}
                                  </div>
                                  <p className="font-medium">{customer.name}</p>
                                </div>
                              </td>
                              <td className="p-4">{customer.email}</td>
                              <td className="p-4 hidden md:table-cell">{new Date(customer.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Content Tab */}
            <TabsContent value="shipping">
              <ShippingZonesManager />
            </TabsContent>

            <TabsContent value="ai-content">
              <AIContentManager />
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content">
              <Card className="luxury-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    AI-Powered Content Management
                  </CardTitle>
                  <CardDescription>
                    Generate and manage page content with AI assistance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {['about', 'community', 'faq', 'contact', 'shipping', 'privacy', 'terms'].map(pageId => (
                      <Card key={pageId} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setEditingContent(pageId)}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold capitalize">{pageId} Page</h3>
                              <p className="text-sm text-muted-foreground">Click to edit</p>
                            </div>
                            <Edit className="text-accent" size={20} />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {editingContent && (
                    <div className="mt-8 p-6 bg-secondary/20 rounded-xl">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold capitalize">Editing: {editingContent} Page</h3>
                        <Button variant="outline" onClick={() => setEditingContent(null)}>Close</Button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label>Page Title</Label>
                          <Input
                            value={contentForm.title}
                            onChange={e => setContentForm({...contentForm, title: e.target.value})}
                            placeholder={`${editingContent} Page Title`}
                          />
                        </div>
                        <div>
                          <Label>Content (JSON or text)</Label>
                          <Textarea
                            value={typeof contentForm.content === 'object' ? JSON.stringify(contentForm.content, null, 2) : contentForm.content}
                            onChange={e => {
                              try {
                                setContentForm({...contentForm, content: JSON.parse(e.target.value)});
                              } catch {
                                setContentForm({...contentForm, content: { text: e.target.value }});
                              }
                            }}
                            rows={10}
                            placeholder="Enter page content..."
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleGenerateContent(editingContent)} disabled={generating} variant="outline">
                            <Sparkles className="w-4 h-4 mr-2" />
                            {generating ? 'Generating...' : 'Generate with AI'}
                          </Button>
                          <Button onClick={() => handleSaveContent(editingContent)} className="btn-primary">
                            <Save className="w-4 h-4 mr-2" />Save Content
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
